
import { useState, useCallback, useMemo } from 'react'
import { StageWrapper } from '@/features/enrollment/core/StageWrapper'
import type { ValidationErrors } from '@/features/enrollment'
import { Loader2, CheckCircle, XCircle, AlertTriangle, Shield, AlertCircle } from 'lucide-react'
import Cookies from 'js-cookie'
import { formatPhone } from '@/shared/utils/formatters/phoneUtils'
import { useAuth } from '@/features/auth/hooks/useAuth'

// Age eligibility constants for ACA Health Insurance
const DEFAULT_MIN_AGE = 19
const DEFAULT_MAX_AGE = 64

const STATES = [
    "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut", "Delaware",
    "District of Columbia", "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa",
    "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota",
    "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire", "New Jersey",
    "New Mexico", "New York", "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon",
    "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota", "Tennessee", "Texas", "Utah",
    "Vermont", "Virginia", "Washington", "West Virginia", "Wisconsin", "Wyoming"
]

// State code mapping for API calls
const STATE_TO_CODE: Record<string, string> = {
    "Alabama": "AL", "Alaska": "AK", "Arizona": "AZ", "Arkansas": "AR", "California": "CA",
    "Colorado": "CO", "Connecticut": "CT", "Delaware": "DE", "District of Columbia": "DC",
    "Florida": "FL", "Georgia": "GA", "Hawaii": "HI", "Idaho": "ID", "Illinois": "IL",
    "Indiana": "IN", "Iowa": "IA", "Kansas": "KS", "Kentucky": "KY", "Louisiana": "LA",
    "Maine": "ME", "Maryland": "MD", "Massachusetts": "MA", "Michigan": "MI", "Minnesota": "MN",
    "Mississippi": "MS", "Missouri": "MO", "Montana": "MT", "Nebraska": "NE", "Nevada": "NV",
    "New Hampshire": "NH", "New Jersey": "NJ", "New Mexico": "NM", "New York": "NY",
    "North Carolina": "NC", "North Dakota": "ND", "Ohio": "OH", "Oklahoma": "OK", "Oregon": "OR",
    "Pennsylvania": "PA", "Rhode Island": "RI", "South Carolina": "SC", "South Dakota": "SD",
    "Tennessee": "TN", "Texas": "TX", "Utah": "UT", "Vermont": "VT", "Virginia": "VA",
    "Washington": "WA", "West Virginia": "WV", "Wisconsin": "WI", "Wyoming": "WY"
}

interface VerificationResult {
    stateEligibility: 'eligible' | 'not_eligible' | null
    availableCarriers: Array<{ id: string; name: string; code: string; commissionRate: number | null }>
    clientEligibility: {
        status: 'eligible' | 'potentially_eligible' | 'potentially_risky' | 'not_eligible' | 'excommunicado'
        message: string
        details?: string
    } | null
}

interface ClientValidationStepProps {
    enrollmentData: any
    updateEnrollmentData: (field: string, value: any) => void
    handleNavigate: (targetIndex: number) => void | Promise<void>
    handleNavigateWithCompletion?: (targetIndex: number) => Promise<void>
    isNavigating: boolean
    currentStepIndex?: number
    totalSteps?: number
    campaignId?: string
    [key: string]: any
}

export default function ClientValidationStep({
    enrollmentData,
    updateEnrollmentData,
    handleNavigate,
    handleNavigateWithCompletion,
    isNavigating,
    currentStepIndex,
    totalSteps = 6,
    campaignId,
    isMasterMode = false,
    revealAllFields = false
}: ClientValidationStepProps) {
    const { user } = useAuth()
    const [errors, setErrors] = useState<ValidationErrors>({})
    const [isVerifying, setIsVerifying] = useState(false)
    const [verificationResult, setVerificationResult] = useState<VerificationResult | null>(null)
    const [verificationComplete, setVerificationComplete] = useState(false)

    // Calculate age from DOB
    const calculateAge = useCallback((dob: string): number | null => {
        if (!dob) return null
        const birthDate = new Date(dob)
        const today = new Date()
        let age = today.getFullYear() - birthDate.getFullYear()
        const monthDiff = today.getMonth() - birthDate.getMonth()
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--
        }
        return age
    }, [])

    const campaignConfig = useMemo(() => enrollmentData.campaign || {}, [enrollmentData.campaign]);
    const minAge = campaignConfig.minAge || DEFAULT_MIN_AGE;
    const maxAge = campaignConfig.maxAge || DEFAULT_MAX_AGE;
    const allowedStates = useMemo(() => {
        const states = campaignConfig.allowedStates;
        if (Array.isArray(states)) return states;
        if (typeof states === 'string') {
            try { return JSON.parse(states); } catch { return [states]; }
        }
        return null;
    }, [campaignConfig.allowedStates]);

    const filteredStates = useMemo(() => {
        if (!allowedStates) return STATES;
        return STATES.filter(state => {
            const code = STATE_TO_CODE[state] || state;
            return allowedStates.includes(code.toUpperCase());
        });
    }, [allowedStates]);

    const clientAge = useMemo(() => calculateAge(enrollmentData.clientDOB), [calculateAge, enrollmentData.clientDOB])
    const isAgeValid = clientAge !== null && clientAge >= minAge && clientAge <= maxAge
    const showAgeError = enrollmentData.clientDOB && !isAgeValid

    // Real-time phone validation
    const phoneDigits = useMemo(() => (enrollmentData.clientPhone || '').replace(/\D/g, ''), [enrollmentData.clientPhone])
    const isPhoneValid = phoneDigits.length === 10
    const showPhoneError = phoneDigits.length > 0 && phoneDigits.length < 10

    // Real-time email validation
    const emailValue = enrollmentData.clientEmail || ''
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    const isEmailValid = emailRegex.test(emailValue)
    const showEmailError = emailValue.length > 0 && !isEmailValid

    const validateFields = useCallback((): ValidationErrors => {
        const validationErrors: ValidationErrors = {}

        if (!enrollmentData.clientFirstName) {
            validationErrors.clientFirstName = 'Client first name is required'
        }
        if (!enrollmentData.clientLastName) {
            validationErrors.clientLastName = 'Client last name is required'
        }
        if (!enrollmentData.clientDOB) {
            validationErrors.clientDOB = 'Date of birth is required'
        } else {
            const age = calculateAge(enrollmentData.clientDOB)
            if (age !== null && (age < minAge || age > maxAge)) {
                if (age < minAge) {
                    validationErrors.clientDOB = `Too young: Client must be at least ${minAge} years old`
                } else {
                    validationErrors.clientDOB = `Too old: Client must be ${maxAge} years old or younger`
                }
            }
        }
        if (!enrollmentData.clientSSN) {
            validationErrors.clientSSN = 'SSN is required'
        } else if (enrollmentData.clientSSN.replace(/\D/g, '').length !== 9) {
            validationErrors.clientSSN = 'SSN must be 9 digits'
        }
        if (!enrollmentData.clientState) {
            validationErrors.clientState = 'State is required'
        } else if (allowedStates && !allowedStates.includes(STATE_TO_CODE[enrollmentData.clientState] || enrollmentData.clientState)) {
            validationErrors.clientState = `Not supported state: ${enrollmentData.clientState} is not currently supported`
        }
        if (!enrollmentData.clientZip) {
            validationErrors.clientZip = 'Zip code is required'
        } else if (!/^\d{5}$/.test(enrollmentData.clientZip)) {
            validationErrors.clientZip = 'Please enter a 5-digit zip code'
        }

        // Phone number validation
        if (!enrollmentData.clientPhone) {
            validationErrors.clientPhone = 'Phone number is required'
        } else {
            const phoneDigits = enrollmentData.clientPhone.replace(/\D/g, '')
            if (phoneDigits.length !== 10) {
                validationErrors.clientPhone = 'Phone number must be 10 digits'
            }
        }

        // Email validation
        if (!enrollmentData.clientEmail) {
            validationErrors.clientEmail = 'Email address is required'
        } else {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
            if (!emailRegex.test(enrollmentData.clientEmail)) {
                validationErrors.clientEmail = 'Please enter a valid email address (e.g., name@domain.com)'
            }
        }

        return validationErrors
    }, [enrollmentData, calculateAge, minAge, maxAge, allowedStates])

    const handleVerify = useCallback(async () => {
        const validationErrors = validateFields()
        setErrors(validationErrors)

        if (Object.keys(validationErrors).length > 0) {
            return
        }

        setIsVerifying(true)

        try {
            const stateCode = STATE_TO_CODE[enrollmentData.clientState] || enrollmentData.clientState
            // Get campaignId from props or enrollmentData
            const activeCampaignId = campaignId || enrollmentData.campaignId || enrollmentData.campaign?.id

            console.log('🔍 [ClientValidation] Verification params:', {
                stateCode,
                activeCampaignId,
                campaignIdProp: campaignId,
                enrollmentCampaignId: enrollmentData.campaignId,
                enrollmentCampaign: enrollmentData.campaign
            })

            // 1. Check state eligibility (is state allowed for this campaign)
            const stateEligibilityResult = checkStateEligibility(activeCampaignId, stateCode)

            // 2. Add USPS ZIP verification
            const zipVerification = verifyZipcodeWithUSPS(enrollmentData.clientZip, stateCode)

            // 3. Call remaining APIs in parallel
            const [stateElig, zipRes, carriersResult, clientEligibilityResult] = await Promise.all([
                stateEligibilityResult,
                zipVerification,
                getAvailableCarriers(activeCampaignId, stateCode),
                checkClientEligibility(enrollmentData.clientSSN, enrollmentData.clientDOB)
            ])

            if (!zipRes.isValid) {
                setErrors({ clientZip: zipRes.error || 'Invalid ZIP code for selected state' })
                setIsVerifying(false)
                return
            }

            const result: VerificationResult = {
                stateEligibility: stateElig,
                availableCarriers: carriersResult,
                clientEligibility: clientEligibilityResult
            }

            setVerificationResult(result)
            setVerificationComplete(true)

            // Store verification data in enrollment data
            updateEnrollmentData('verificationComplete', true)
            updateEnrollmentData('stateEligibility', result.stateEligibility)
            updateEnrollmentData('availableCarriers', result.availableCarriers)
            updateEnrollmentData('clientEligibilityStatus', result.clientEligibility?.status)
            updateEnrollmentData('clientEligibilityMessage', result.clientEligibility?.message)
            updateEnrollmentData('clientCity', zipRes.city)

        } catch (error) {
            console.error('Verification error:', error)
            setErrors({ general: 'Verification failed. Please try again.' })
        } finally {
            setIsVerifying(false)
        }
    }, [enrollmentData, campaignId, validateFields, updateEnrollmentData])

    const handleNext = useCallback(async (): Promise<boolean> => {
        if (!verificationComplete) {
            setErrors({ general: 'Please complete verification before continuing' })
            return false
        }

        const validationErrors = validateFields()
        setErrors(validationErrors)
        return Object.keys(validationErrors).length === 0
    }, [verificationComplete, validateFields])

    // Simple SSN formatter
    const handleSSNChange = (val: string) => {
        const nums = val.replace(/\D/g, '').substring(0, 9)
        let formatted = nums
        if (nums.length > 5) {
            formatted = `${nums.substring(0, 3)}-${nums.substring(3, 5)}-${nums.substring(5, 9)}`
        } else if (nums.length > 3) {
            formatted = `${nums.substring(0, 3)}-${nums.substring(3, 5)}`
        }
        updateEnrollmentData('clientSSN', formatted)
        // Reset verification when SSN changes
        if (verificationComplete) {
            setVerificationComplete(false)
            setVerificationResult(null)
        }
    }

    // Phone number formatter
    const handlePhoneChange = (val: string) => {
        updateEnrollmentData('clientPhone', formatPhone(val))
    }

    // Name capitalization helper
    const handleNameChange = (field: string, val: string) => {
        const capitalized = val.replace(/\b\w/g, char => char.toUpperCase())
        updateEnrollmentData(field, capitalized)
    }

    const getEligibilityStatusColor = (status: string) => {
        switch (status) {
            case 'eligible': return 'text-green-600 bg-green-50 border-green-200'
            case 'potentially_eligible': return 'text-yellow-600 bg-yellow-50 border-yellow-200'
            case 'potentially_risky': return 'text-orange-600 bg-orange-50 border-orange-200'
            case 'not_eligible': return 'text-red-600 bg-red-50 border-red-200'
            case 'excommunicado': return 'text-red-800 bg-red-100 border-red-300'
            default: return 'text-gray-600 bg-gray-50 border-gray-200'
        }
    }

    const getEligibilityIcon = (status: string) => {
        switch (status) {
            case 'eligible': return <CheckCircle className="w-5 h-5 text-green-600" />
            case 'potentially_eligible': return <AlertTriangle className="w-5 h-5 text-yellow-600" />
            case 'potentially_risky': return <AlertTriangle className="w-5 h-5 text-orange-600" />
            case 'not_eligible': return <XCircle className="w-5 h-5 text-red-600" />
            case 'excommunicado': return <XCircle className="w-5 h-5 text-red-800" />
            default: return null
        }
    }

    return (
        <StageWrapper
            currentStepIndex={currentStepIndex ?? 0}
            totalSteps={totalSteps}
            handleNavigate={handleNavigate}
            handleNavigateWithCompletion={handleNavigateWithCompletion}
            onNext={handleNext}
            isNavigating={isNavigating}
            showBackButton={false}
        >
            <div className="card">
                <div className="flex items-center space-x-3 mb-6">
                    <div className="w-12 h-12 bg-secondary-100 rounded-lg flex items-center justify-center">
                        <svg className="w-6 h-6 text-secondary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                    </div>
                    <div>
                        <h3 className="text-xl font-semibold text-accent-900">Step 1: Client Validation</h3>
                        <p className="text-sm text-accent-600">Enter client details and verify eligibility.</p>
                    </div>
                </div>

                {/* Master Mode & Reveal All: System Verification Monitor */}
                {(isMasterMode || revealAllFields) && (
                    <div className="mb-8 p-5 bg-amber-50 border-2 border-amber-200 rounded-xl shadow-inner animate-in fade-in slide-in-from-top-2 duration-300">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                                <Shield className="w-5 h-5 text-amber-700" />
                                <h4 className="text-sm font-black text-amber-900 uppercase tracking-tighter">
                                    {revealAllFields && !isMasterMode ? 'Extended Verification Data' : 'System Verification Monitor'}
                                </h4>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-[10px] font-bold text-amber-600 uppercase">Master Override:</span>
                                <button
                                    onClick={() => {
                                        setVerificationComplete(true);
                                        updateEnrollmentData('verificationComplete', true);
                                    }}
                                    className="px-2 py-0.5 bg-amber-200 hover:bg-amber-300 text-amber-800 rounded text-[10px] font-bold transition-colors"
                                >
                                    Force Verified
                                </button>
                                <button
                                    onClick={() => {
                                        setVerificationComplete(false);
                                        setVerificationResult(null);
                                        updateEnrollmentData('verificationComplete', false);
                                    }}
                                    className="px-2 py-0.5 bg-red-100 hover:bg-red-200 text-red-700 rounded text-[10px] font-bold transition-colors"
                                >
                                    Reset
                                </button>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="space-y-3 col-span-1">
                                <div>
                                    <p className="text-[10px] font-bold text-amber-600 uppercase">Verification Result (Raw)</p>
                                    <div className="mt-1 bg-white border border-amber-100 rounded-md p-3 max-h-[150px] overflow-auto">
                                        <pre className="text-[11px] font-mono leading-tight text-amber-800">
                                            {JSON.stringify(verificationResult || { status: 'Pending Verification' }, null, 2)}
                                        </pre>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-3 col-span-1">
                                <div>
                                    <p className="text-[10px] font-bold text-amber-600 uppercase">Active Configuration</p>
                                    <div className="mt-1 p-3 bg-white rounded-lg border border-amber-100 space-y-2">
                                        <div className="flex justify-between items-center text-[11px]">
                                            <span className="text-amber-700 font-bold">Min Age:</span>
                                            <span className="text-amber-900 font-mono">{minAge}</span>
                                        </div>
                                        <div className="flex justify-between items-center text-[11px]">
                                            <span className="text-amber-700 font-bold">Max Age:</span>
                                            <span className="text-amber-900 font-mono">{maxAge}</span>
                                        </div>
                                        <div className="pt-1 border-t border-amber-50">
                                            <p className="text-[9px] text-amber-500 font-bold uppercase mb-1">Allowed States:</p>
                                            <p className="text-[10px] text-amber-900 font-mono break-all leading-none">
                                                {allowedStates ? JSON.stringify(allowedStates) : 'ALL'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-3 col-span-1">
                                <div className="p-3 bg-white rounded-lg border border-amber-100">
                                    <p className="text-[10px] font-bold text-amber-600 uppercase mb-2">Internal Flags</p>
                                    <div className="space-y-1">
                                        <div className="flex justify-between items-center text-[11px]">
                                            <span className="text-amber-700">Phone Valid:</span>
                                            <span className={`font-bold ${isPhoneValid ? 'text-green-600' : 'text-red-500'}`}>
                                                {isPhoneValid ? 'TRUE' : 'FALSE'} ({phoneDigits.length})
                                            </span>
                                        </div>
                                        <div className="flex justify-between items-center text-[11px]">
                                            <span className="text-amber-700">Email Valid:</span>
                                            <span className={`font-bold ${isEmailValid ? 'text-green-600' : 'text-red-500'}`}>
                                                {isEmailValid ? 'TRUE' : 'FALSE'}
                                            </span>
                                        </div>
                                        <div className="flex justify-between items-center text-[11px]">
                                            <span className="text-amber-700">Zip:</span>
                                            <span className="text-amber-900 font-mono">{enrollmentData.clientZip || 'N/A'}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="mt-4 pt-4 border-t border-amber-100 grid grid-cols-2 gap-4">
                            <div>
                                <p className="text-[10px] font-bold text-amber-600 uppercase">Input Normalization</p>
                                <div className="mt-1 flex gap-2">
                                    <div className="px-2 py-1 bg-white rounded border border-amber-100 flex-1">
                                        <span className="block text-[8px] text-amber-500 font-bold">STATE</span>
                                        <span className="text-[10px] font-mono font-bold text-amber-900">{STATE_TO_CODE[enrollmentData.clientState] || 'N/A'}</span>
                                    </div>
                                    <div className="px-2 py-1 bg-white rounded border border-amber-100 flex-1">
                                        <span className="block text-[8px] text-amber-500 font-bold">CLEAN PHONE</span>
                                        <span className="text-[10px] font-mono font-bold text-amber-900">{phoneDigits || 'N/A'}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="p-2 bg-amber-100/50 rounded-lg border border-amber-200">
                                <p className="text-[10px] font-bold text-amber-600 uppercase mb-1 leading-none italic">Calibration Rule</p>
                                <p className="text-[10px] text-amber-800">
                                    {revealAllFields && !isMasterMode
                                        ? 'Reveal mode enables visibility into hidden transcription markers and system metadata.'
                                        : 'Enrollment UUID is generated at start. Use Force Verified to skip backend health checks.'}
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                <div className="space-y-6">
                    {/* Client Details Section */}
                    <div>
                        <h4 className="text-md font-semibold text-accent-800 mb-4">Client Details</h4>
                        <div className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Client First Name <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={enrollmentData.clientFirstName || ''}
                                        onChange={(e) => handleNameChange('clientFirstName', e.target.value)}
                                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.clientFirstName ? 'border-red-500' : 'border-gray-300'}`}
                                        placeholder="Enter First Name"
                                    />
                                    {errors.clientFirstName && (
                                        <p className="mt-1 text-sm text-red-600">{errors.clientFirstName}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Client Last Name <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={enrollmentData.clientLastName || ''}
                                        onChange={(e) => handleNameChange('clientLastName', e.target.value)}
                                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.clientLastName ? 'border-red-500' : 'border-gray-300'}`}
                                        placeholder="Enter Last Name"
                                    />
                                    {errors.clientLastName && (
                                        <p className="mt-1 text-sm text-red-600">{errors.clientLastName}</p>
                                    )}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Date of Birth <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="date"
                                        value={enrollmentData.clientDOB || ''}
                                        onChange={(e) => {
                                            updateEnrollmentData('clientDOB', e.target.value)
                                            if (verificationComplete) {
                                                setVerificationComplete(false)
                                                setVerificationResult(null)
                                            }
                                        }}
                                        max={new Date().toISOString().split('T')[0]}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                    {showAgeError && clientAge !== null && (
                                        <p className="mt-2 text-sm text-red-600">
                                            Age: {clientAge} years old - NOT Eligible.
                                            {clientAge < minAge && ` Too young: Must be at least ${minAge} years old.`}
                                            {clientAge > maxAge && ` Too old: Must be ${maxAge} years old or younger.`}
                                        </p>
                                    )}
                                    {errors.clientDOB && !enrollmentData.clientDOB && (
                                        <p className="mt-1 text-sm text-red-600">{errors.clientDOB}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Social Security Number <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={enrollmentData.clientSSN || ''}
                                        onChange={(e) => handleSSNChange(e.target.value)}
                                        placeholder="###-##-####"
                                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.clientSSN ? 'border-red-500' : 'border-gray-300'}`}
                                    />
                                    {errors.clientSSN && (
                                        <p className="mt-1 text-sm text-red-600">{errors.clientSSN}</p>
                                    )}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        State <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        value={enrollmentData.clientState || ''}
                                        onChange={(e) => {
                                            updateEnrollmentData('clientState', e.target.value)
                                            if (verificationComplete) {
                                                setVerificationComplete(false)
                                                setVerificationResult(null)
                                            }
                                        }}
                                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.clientState ? 'border-red-500' : 'border-gray-300'}`}
                                    >
                                        <option value="">Select State</option>
                                        {filteredStates.map(state => (
                                            <option key={state} value={state}>{state}</option>
                                        ))}
                                    </select>
                                    {errors.clientState && (
                                        <p className="mt-1 text-sm text-red-600">{errors.clientState}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Zip Code <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={enrollmentData.clientZip || ''}
                                        onChange={(e) => {
                                            const val = e.target.value.replace(/\D/g, '').substring(0, 5)
                                            updateEnrollmentData('clientZip', val)
                                        }}
                                        placeholder="12345"
                                        maxLength={5}
                                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.clientZip ? 'border-red-500' : 'border-gray-300'}`}
                                    />
                                    {errors.clientZip && (
                                        <p className="mt-1 text-sm text-red-600">{errors.clientZip}</p>
                                    )}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Phone Number <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="tel"
                                        value={enrollmentData.clientPhone || ''}
                                        onChange={(e) => handlePhoneChange(e.target.value)}
                                        placeholder="(555) 555-5555"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                    {showPhoneError && (
                                        <p className="mt-1 text-sm text-red-600">
                                            Phone number must be 10 digits ({phoneDigits.length}/10 entered)
                                        </p>
                                    )}
                                    {errors.clientPhone && !enrollmentData.clientPhone && (
                                        <p className="mt-1 text-sm text-red-600">{errors.clientPhone}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Email Address <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="email"
                                        value={enrollmentData.clientEmail || ''}
                                        onChange={(e) => updateEnrollmentData('clientEmail', e.target.value)}
                                        placeholder="name@domain.com"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                    {showEmailError && (
                                        <p className="mt-1 text-sm text-red-600">
                                            Please enter a valid email address (e.g., name@domain.com)
                                        </p>
                                    )}
                                    {errors.clientEmail && !enrollmentData.clientEmail && (
                                        <p className="mt-1 text-sm text-red-600">{errors.clientEmail}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Verify Button */}
                    <div className="border-t pt-6">
                        <button
                            onClick={handleVerify}
                            disabled={isVerifying || showAgeError}
                            className={`w-full py-3 px-4 rounded-lg font-semibold text-white transition-all duration-200 flex items-center justify-center gap-2 ${isVerifying || showAgeError
                                ? 'bg-gray-400 cursor-not-allowed'
                                : verificationComplete
                                    ? 'bg-green-600 hover:bg-green-700'
                                    : 'bg-indigo-600 hover:bg-indigo-700'
                                }`}
                        >
                            {isVerifying ? (
                                <>
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                    Verifying...
                                </>
                            ) : verificationComplete ? (
                                <>
                                    <CheckCircle className="w-5 h-5" />
                                    Verified - Click to Re-verify
                                </>
                            ) : (
                                <>
                                    <Shield className="w-5 h-5" />
                                    Verify Client Eligibility
                                </>
                            )}
                        </button>
                        {errors.general && (
                            <p className="mt-2 text-sm text-red-600 text-center">{errors.general}</p>
                        )}
                        {showAgeError && (
                            <p className="mt-2 text-sm text-red-600 text-center font-medium">
                                Cannot verify: Client age ({clientAge} years) is outside the eligible range ({minAge}-{maxAge})
                            </p>
                        )}
                    </div>

                    {/* Verification Results Section */}
                    {verificationResult && (
                        <div className="border-t pt-6">
                            <h4 className="text-lg font-bold text-accent-900 mb-4 flex items-center gap-2">
                                <Shield className="w-5 h-5" />
                                VERIFICATION RESULTS
                            </h4>

                            <div className="space-y-4">
                                {/* Residential State Status */}
                                <div className={`p-4 rounded-lg border ${verificationResult.stateEligibility === 'eligible'
                                    ? 'bg-green-50 border-green-200'
                                    : 'bg-red-50 border-red-200'
                                    }`}>
                                    <div className="flex items-center justify-between">
                                        <span className="font-semibold text-gray-800">RESIDENTIAL STATE STATUS:</span>
                                        <span className={`font-bold px-3 py-1 rounded-full text-sm ${verificationResult.stateEligibility === 'eligible'
                                            ? 'bg-green-200 text-green-800'
                                            : 'bg-red-200 text-red-800'
                                            }`}>
                                            {verificationResult.stateEligibility === 'eligible' ? 'Eligible' : 'Not Eligible'}
                                        </span>
                                    </div>
                                </div>

                                {/* Available Carriers */}
                                <div className="p-4 rounded-lg border border-blue-200 bg-blue-50">
                                    <span className="font-semibold text-gray-800 block mb-3">Available Carriers:</span>
                                    {verificationResult.availableCarriers.length > 0 ? (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                            {verificationResult.availableCarriers.map((carrier) => (
                                                <div key={carrier.id} className="flex items-center justify-between bg-white p-2 rounded border border-blue-100">
                                                    <span className="font-medium text-gray-700">{carrier.name}</span>
                                                    {carrier.commissionRate !== null && (isMasterMode || user?.role === 'fmo_ceo' || user?.role === 'sales_manager' || user?.role === 'admin') && (
                                                        <span className="text-green-600 font-semibold">${carrier.commissionRate}</span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-gray-600 italic">No carriers available for this state</p>
                                    )}
                                </div>

                                {/* Client Eligibility Checker */}
                                {verificationResult.clientEligibility && (
                                    <div className={`p-4 rounded-lg border ${getEligibilityStatusColor(verificationResult.clientEligibility.status)}`}>
                                        <div className="flex items-start gap-3">
                                            {getEligibilityIcon(verificationResult.clientEligibility.status)}
                                            <div className="flex-1">
                                                <span className="font-semibold text-gray-800 block">Client Eligibility Checker:</span>
                                                <p className="font-bold mt-1">{verificationResult.clientEligibility.message}</p>
                                                {verificationResult.clientEligibility.details && (
                                                    <p className="text-sm mt-1 opacity-80">{verificationResult.clientEligibility.details}</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </StageWrapper>
    )
}

// API Base URL - use environment variable or default to localhost
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api'

// Helper to get auth headers
function getAuthHeaders(): HeadersInit {
    const token = Cookies.get('token')
    return token ? { 'Authorization': `Bearer ${token}` } : {}
}

// API helper functions (these will call the backend)
async function checkStateEligibility(campaignId: string | undefined, stateCode: string): Promise<'eligible' | 'not_eligible'> {
    try {
        if (!campaignId) return 'eligible' // Default to eligible if no campaign ID

        const response = await fetch(`${API_BASE_URL}/campaigns/${campaignId}/state-eligibility?state=${stateCode}`, {
            headers: getAuthHeaders()
        })
        if (!response.ok) {
            // If API doesn't exist yet, default to eligible
            return 'eligible'
        }
        const data = await response.json()
        return data.isEligible ? 'eligible' : 'not_eligible'
    } catch (error) {
        console.error('State eligibility check failed:', error)
        return 'eligible' // Default to eligible on error
    }
}

async function getAvailableCarriers(campaignId: string | undefined, stateCode: string): Promise<Array<{ id: string; name: string; code: string; commissionRate: number | null }>> {
    try {
        if (!campaignId) return []

        const response = await fetch(`${API_BASE_URL}/insurance-carriers/campaign/${campaignId}/state/${stateCode}/available`, {
            headers: getAuthHeaders()
        })
        if (!response.ok) {
            console.error('Get available carriers response not ok:', response.status, response.statusText)
            return []
        }
        const result = await response.json()
        // Backend returns { success: true, data: [...carriers...] }
        return result.data || []
    } catch (error) {
        console.error('Get available carriers failed:', error)
        return []
    }
}

async function checkClientEligibility(ssn: string, dob: string): Promise<{
    status: 'eligible' | 'potentially_eligible' | 'potentially_risky' | 'not_eligible' | 'excommunicado'
    message: string
    details?: string
}> {
    try {
        const response = await fetch(`${API_BASE_URL}/enrollments/check-client-eligibility`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...getAuthHeaders()
            },
            body: JSON.stringify({ ssn: ssn.replace(/\D/g, ''), dob })
        })

        if (!response.ok) {
            // If API doesn't exist yet, return eligible
            return {
                status: 'eligible',
                message: 'Eligible - No Client Found'
            }
        }

        const data = await response.json()
        return data
    } catch (error) {
        console.error('Client eligibility check failed:', error)
        // Default to eligible if API fails
        return {
            status: 'eligible',
            message: 'Eligible - No Client Found'
        }
    }
}

async function verifyZipcodeWithUSPS(zipcode: string, state: string): Promise<{ isValid: boolean; error?: string; city?: string }> {
    try {
        const response = await fetch(`${API_BASE_URL}/enrollments/verify-zipcode`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...getAuthHeaders()
            },
            body: JSON.stringify({ zipcode, state })
        })

        if (!response.ok) {
            return { isValid: true } // Fallback if API is not ready
        }

        const result = await response.json()
        return result.data
    } catch (error) {
        console.error('ZIP code verification failed:', error)
        return { isValid: true }
    }
}
